/**
 * richText.js — Cross-surface markdown normalization utility
 *
 * All rendering surfaces that display LLM-generated lesson text MUST pass
 * raw content through one of these functions before displaying it to users.
 * This prevents raw markdown syntax (**, *, #, -) from appearing as literal
 * characters in DOM text nodes, PPTX slides, Canvas exports, or student packets.
 *
 * Usage:
 *   import { stripMarkdown, renderMarkdownInline, normalizeBullets } from '../utils/richText';
 *
 * Safe for use in JSX:  renderMarkdownInline(text)  → React elements
 * Safe for plain text:  stripMarkdown(text)          → string
 * Safe for bullet lists: normalizeBullets(text)      → string[]
 */

import React from 'react';

// ---------------------------------------------------------------------------
// stripMarkdown(text) → string
//
// Removes all markdown formatting characters, returning plain text.
// Use this wherever text is rendered as a DOM text node (non-JSX context)
// or wherever you need a clean string for an aria-label, title attr, etc.
//
// Handles: **bold**, *italic*, __bold__, _italic_, # headings, `code`,
//          - bullet, * bullet, • bullet, > blockquote
// ---------------------------------------------------------------------------
export function stripMarkdown(text) {
  if (!text || typeof text !== 'string') return text ?? '';

  return text
    // Remove heading markers (# ## ### etc.) at line start
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold: **text** or __text__
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/__(.+?)__/gs, '$1')
    // Remove italic: *text* or _text_
    .replace(/\*(.+?)\*/gs, '$1')
    .replace(/_(.+?)_/gs, '$1')
    // Remove inline code: `code`
    .replace(/`(.+?)`/g, '$1')
    // Remove leading bullet markers: - , * , • at start of line
    .replace(/^[\s]*[•\-*]\s+/gm, '')
    // Remove blockquote markers: > at line start
    .replace(/^>\s*/gm, '')
    // Collapse multiple blank lines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---------------------------------------------------------------------------
// renderMarkdownInline(text) → React elements
//
// Converts inline markdown to React elements with semantic HTML:
//   **bold**   → <strong>
//   *italic*   → <em>
//   `code`     → <code>
//   # Heading  → stripped (heading markers removed, text preserved)
//
// Bullet structure is preserved: lines starting with -, *, • are rendered
// as <li> inside a <ul>.
//
// Safe for use inside JSX — returns React.Fragment or array of nodes.
// Areas already using ReactMarkdown are safe; annotate those call-sites
// with // [richText: already normalized via ReactMarkdown — do not re-process]
// ---------------------------------------------------------------------------
export function renderMarkdownInline(text) {
  if (!text || typeof text !== 'string') return null;

  // Strip heading markers but keep the heading text
  const cleaned = text.replace(/^#{1,6}\s+/gm, '');

  const lines = cleaned.split('\n');
  const bulletPattern = /^[\s]*[•\-*]\s+(.*)/;

  // Check if we have a bullet list
  const hasBullets = lines.some(l => bulletPattern.test(l));

  if (hasBullets) {
    const items = lines
      .map(line => {
        const m = line.match(bulletPattern);
        return m ? m[1].trim() : line.trim();
      })
      .filter(Boolean);

    return (
      <ul style={{ margin: 0, paddingLeft: '1.25em', listStyle: 'disc' }}>
        {items.map((item, idx) => (
          <li key={idx}>{renderInlineSpans(item)}</li>
        ))}
      </ul>
    );
  }

  // Non-bullet: render paragraph(s) with inline formatting
  const paragraphs = cleaned.split(/\n\n+/).filter(Boolean);
  if (paragraphs.length === 1) {
    return <span>{renderInlineSpans(paragraphs[0])}</span>;
  }
  return (
    <>
      {paragraphs.map((para, idx) => (
        <p key={idx} style={{ margin: '0.25em 0' }}>
          {renderInlineSpans(para)}
        </p>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// normalizeBullets(text) → string[]
//
// Splits text on newlines, strips leading bullet markers (•, -, *, numbered),
// and strips inline markdown from each resulting line.
// Returns a clean array of strings — ready for slide bullet rendering.
// Empty lines are filtered out.
// ---------------------------------------------------------------------------
export function normalizeBullets(text) {
  if (!text || typeof text !== 'string') return [];

  return text
    .split('\n')
    .map(line => {
      // Strip leading bullet markers
      const stripped = line.replace(/^[\s]*[•\-*]\s+/, '').replace(/^\d+\.\s+/, '');
      // Strip inline markdown from the resulting text
      return stripMarkdown(stripped);
    })
    .filter(line => line.trim().length > 0);
}

// ---------------------------------------------------------------------------
// Internal helper — renderInlineSpans(text) → React node[]
//
// Converts **bold**, *italic*, `code` within a single line to React elements.
// Not exported — use renderMarkdownInline() at the surface level.
// ---------------------------------------------------------------------------
function renderInlineSpans(text) {
  if (!text) return null;

  // Tokenize: split on **bold**, *italic*, `code`
  // Order matters: ** must be matched before * to avoid greedy single-star match
  const parts = [];
  let remaining = text;
  let key = 0;

  const patterns = [
    { regex: /\*\*(.+?)\*\*/s, tag: 'strong' },
    { regex: /__(.+?)__/s,    tag: 'strong' },
    { regex: /\*(.+?)\*/s,    tag: 'em' },
    { regex: /_(.+?)_/s,      tag: 'em' },
    { regex: /`(.+?)`/,       tag: 'code' },
  ];

  while (remaining.length > 0) {
    // Find the earliest matching pattern
    let earliest = null;
    let earliestIndex = Infinity;

    for (const { regex, tag } of patterns) {
      const m = remaining.match(regex);
      if (m && m.index < earliestIndex) {
        earliest = { match: m, tag };
        earliestIndex = m.index;
      }
    }

    if (!earliest) {
      parts.push(remaining);
      break;
    }

    const { match, tag } = earliest;
    // Text before the match
    if (match.index > 0) {
      parts.push(remaining.slice(0, match.index));
    }
    // The matched element
    const inner = match[1];
    parts.push(React.createElement(tag, { key: key++ }, inner));
    // Continue after the match
    remaining = remaining.slice(match.index + match[0].length);
  }

  return parts.length === 1 && typeof parts[0] === 'string'
    ? parts[0]
    : parts;
}
